/**
 * MỞ BIỂU MẪU TRÊN MÀN CẢM ỨNG KHÔNG BẬT BÀN PHÍM ẢO.
 *
 * ── VẤN ĐỀ NÀY GIẢI ─────────────────────────────────────────────────────────
 * Trên điện thoại / máy POS cảm ứng, gần như mọi biểu mẫu vừa mở đã bật bàn phím ảo: bàn phím che
 * nửa màn, người dùng chưa kịp nhìn biểu mẫu có những ô nào đã phải gõ vào ô đầu tiên. Hai nguồn:
 *
 * | Nguồn | Cơ chế |
 * |---|---|
 * | Radix `Dialog` / `Sheet` / `Popover` | Mặc định `onOpenAutoFocus` đưa tiêu điểm vào phần tử bấm được ĐẦU TIÊN trong lớp nổi — thường là ô nhập đầu tiên. |
 * | Prop `autoFocus` của `Input` / `Textarea` | React gọi `.focus()` lúc gắn vào DOM. Ô có `autoFocus` đã giữ tiêu điểm thì Radix bỏ qua bước của nó, nên chặn riêng Radix là chưa đủ. |
 *
 * Gói này chặn CẢ HAI ở đúng component gốc — mọi biểu mẫu hiện có và sau này đều thừa hưởng, không
 * màn nào phải nhớ.
 *
 * ── VÌ SAO HỎI `(hover: none) and (pointer: coarse)` CHỨ KHÔNG HỎI BỀ NGANG ─
 * Cùng truy vấn với variant `coarse:` (`src/preset.ts`). Máy POS cảm ứng 15–21" báo viewport ~1920px
 * — theo bề ngang nó là « desktop », mà cả ca làm việc không có con chuột nào. Ngược lại laptop màn
 * cảm ứng khớp `pointer: coarse` nhưng vẫn rê chuột được (`hover: hover`) ⇒ giữ autofocus, vì ở đó
 * bàn phím cứng là đầu vào chính và tự lấy tiêu điểm là tiện.
 *
 * ── CHUỘT / BÀN PHÍM CỨNG: GIỮ NGUYÊN HÀNH VI CŨ ───────────────────────────
 * Không có `window` (SSR) hoặc không khớp truy vấn ⇒ không đụng gì cả.
 */
export const TOUCH_PRIMARY_QUERY = "(hover: none) and (pointer: coarse)";

/**
 * CHÍNH SÁCH: KHÔNG THIẾT BỊ NÀO TỰ NHẢY TIÊU ĐIỂM VÀO Ô GÕ.
 *
 * Bản đầu chỉ chặn trên màn cảm ứng (`isTouchPrimary`). Người dùng vẫn bị làm phiền: hộp thoại mở ra là ô đầu tiên
 * sáng viền, và trên điện thoại bàn phím bật theo — trong khi máy báo `hover: none` không phải lúc nào cũng khớp truy
 * vấn (PWA, WebView, trình duyệt máy POS). Chính sách nay: ở MỌI thiết bị, tiêu điểm chỉ vào ô gõ khi người dùng
 * chạm / bấm phím / ô đã được cho phép (`data-touch-autofocus`). Ngoại lệ bàn phím giữ nguyên: Enter sang ô kế,
 * ô OTP tự nhảy ô, mở hộp thoại bằng phím (≤ 1s sau phím bấm).
 */
export function suppressAutoFocus(): boolean {
  return typeof window !== "undefined";
}

/** Ngón tay là đầu vào CHÍNH (không có thiết bị trỏ rê được). Không có DOM ⇒ `false`. */
export function isTouchPrimary(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(TOUCH_PRIMARY_QUERY).matches;
}

/**
 * Bọc `onOpenAutoFocus` của một lớp nổi Radix.
 *
 * Trên màn cảm ứng: tiêu điểm vào CHÍNH khung lớp nổi (Radix cho nó `tabIndex=-1`) thay vì ô nhập
 * đầu tiên — bẫy tiêu điểm, Escape và trình đọc màn hình vẫn chạy như cũ, chỉ không có ô nhập nào
 * giữ tiêu điểm nên bàn phím ảo không bật. Người dùng chạm vào ô nào thì bàn phím mới lên.
 *
 * Không `preventDefault` mà bỏ trống thì tiêu điểm nằm lại ở nút mở BÊN NGOÀI lớp nổi — Tab đầu tiên
 * và trình đọc màn hình sẽ lạc ra trang phía sau.
 *
 * Nơi gọi tự truyền `onOpenAutoFocus` và đã `preventDefault()` (vd. ô chọn tự quyết tiêu điểm) ⇒
 * tôn trọng, không làm gì thêm.
 */
export function touchSafeOpenAutoFocus(
  handler?: (event: Event) => void,
): (event: Event) => void {
  return (event) => {
    handler?.(event);
    if (event.defaultPrevented || !suppressAutoFocus()) return;
    event.preventDefault();
    const container = event.currentTarget;
    if (container instanceof HTMLElement) container.focus({ preventScroll: true });
  };
}

/**
 * Giá trị `autoFocus` thật sự đưa xuống `<input>` / `<textarea>`.
 *
 * `touchAutoFocus` — ngoại lệ có chủ đích: ô mà người dùng VỪA chạm vào chính vì muốn gõ (vd. bấm ô
 * tìm kiếm thì mở tấm tìm kiếm toàn màn, ô trong tấm đó nên nhận bàn phím ngay).
 */
export function resolveAutoFocus(autoFocus: boolean | undefined, touchAutoFocus: boolean | undefined): boolean | undefined {
  if (!autoFocus || touchAutoFocus) return autoFocus;
  return suppressAutoFocus() ? false : autoFocus;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * LƯỚI CHẶN TOÀN CỤC — `installTouchFocusGuard()`
 *
 * Hai lớp phía trên chỉ phủ component của gói này. Lỗi người dùng vẫn gặp đến từ chỗ khác:
 *
 * | Nguồn lọt lưới | Ví dụ |
 * |---|---|
 * | `<input autoFocus>` thô hoặc Input bản sao riêng của app | ô tìm kiếm đầu trang danh sách |
 * | `.focus()` tự gọi trong `useEffect` / `setTimeout` | ô chọn có tìm kiếm vừa mở là focus ô tìm |
 * | `setFocus` / `shouldFocus` của react-hook-form | biểu mẫu mở ra focus ô lỗi đầu tiên |
 * | Lớp nổi không phải của gói (vaul, bản sao Dialog…) | Radix `FocusScope` tự focus ô đầu tiên |
 *
 * Vá từng chỗ thì chỗ thứ N+1 lại lọt. Cả bốn đều đi qua MỘT cửa: `HTMLElement.prototype.focus`
 * (React thực thi `autoFocus` bằng chính `.focus()` lúc gắn DOM). Lưới bọc cửa đó.
 *
 * ── LUẬT ───────────────────────────────────────────────────────────────────────────────────────
 * Trên màn cảm ứng (`TOUCH_PRIMARY_QUERY`), một lời gọi `.focus()` vào phần tử SẼ BẬT BÀN PHÍM ẢO
 * bị bỏ qua, trừ khi:
 *
 * | Ngoại lệ | Vì sao |
 * |---|---|
 * | Người dùng VỪA chạm chính ô đó / khung bọc nó / `<label>` của nó (≤ 1s) | ô tìm dạng « khung giả » tự `.focus()` ô thật bên trong khi chạm |
 * | Vừa có phím bấm (≤ 1s), hoặc tiêu điểm đang ở một ô gõ được | bàn phím ĐÃ lên rồi: Enter sang ô kế, ô OTP tự nhảy ô |
 * | Phần tử (hoặc tổ tiên) có `data-touch-autofocus` | ngoại lệ có chủ đích — `Input touchAutoFocus` tự gắn |
 *
 * Chạm vào NÚT mở ô chọn rồi ô chọn tự focus ô tìm ⇒ vẫn chặn: nút không chứa ô tìm (ô nằm trong
 * portal). Đó đúng là ca người dùng phàn nàn — muốn gõ tìm thì chạm vào ô tìm, bàn phím mới lên.
 *
 * Chuột / bàn phím cứng / SSR: không cài gì, không đổi hành vi. Gọi nhiều lần: chỉ cài một lần.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

export const TOUCH_AUTOFOCUS_ATTR = "data-touch-autofocus";

/** `type` của `<input>` mà focus vào là bàn phím ảo bật lên. Chọn ngày/màu/tệp mở bộ chọn riêng, không phải bàn phím. */
const KEYBOARD_INPUT_TYPES = new Set(["", "text", "search", "email", "number", "tel", "url", "password"]);

/** Một sự kiện người dùng được coi là « vừa xảy ra » trong khoảng này. */
export const TOUCH_FOCUS_INTENT_WINDOW_MS = 1000;

/** Phần tử này mà nhận tiêu điểm thì bàn phím ảo có bật lên không. */
export function opensVirtualKeyboard(el: Element | null | undefined): boolean {
  if (!el || typeof HTMLElement === "undefined" || !(el instanceof HTMLElement)) return false;
  if (el.inputMode === "none") return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    return !el.readOnly && !el.disabled && KEYBOARD_INPUT_TYPES.has(el.getAttribute("type")?.toLowerCase() ?? "");
  }
  return el.isContentEditable;
}

export type TouchFocusDecisionInput = {
  touchPrimary: boolean;
  opensKeyboard: boolean;
  optedIn: boolean;
  /** Lần chạm gần nhất nhắm vào chính ô / khung bọc / nhãn của nó, trong cửa sổ thời gian. */
  tappedOwn: boolean;
  /** Có phím bấm trong cửa sổ thời gian. */
  keyRecent: boolean;
  /** Tiêu điểm hiện tại đã là một ô gõ được (bàn phím đang mở). */
  keyboardAlreadyUp: boolean;
};

/** Hàm thuần — quyết định có chặn một lời gọi `.focus()` không. */
export function shouldBlockTouchFocus(i: TouchFocusDecisionInput): boolean {
  if (!i.opensKeyboard) return false;
  return !(i.optedIn || i.tappedOwn || i.keyRecent || i.keyboardAlreadyUp);
}

const GUARD_KEY = "__bubeTouchFocusGuard";

function tapTargetsOwn(target: Element, el: HTMLElement): boolean {
  if (!target.isConnected) return false;
  if (target === el || target.contains(el)) return true;
  const label = target.closest("label");
  return !!label && label.control === el;
}

/**
 * Cài lưới chặn — gọi MỘT lần ở điểm vào của app (trước lượt render đầu). Trả hàm gỡ (cho test).
 * Quyết định « có phải màn cảm ứng » đọc LẠI ở từng lời gọi: máy tính bảng gắn/tháo bàn phím chuột.
 */
export function installTouchFocusGuard(): () => void {
  if (typeof window === "undefined" || typeof HTMLElement === "undefined") return () => {};
  const w = window as unknown as Record<string, (() => void) | undefined>;
  const existing = w[GUARD_KEY];
  if (existing) return existing;

  let lastTap: { target: Element; at: number } | null = null;
  let lastKeyAt = Number.NEGATIVE_INFINITY;

  const onPointerDown = (e: Event) => {
    if (e.target instanceof Element) lastTap = { target: e.target, at: performance.now() };
  };
  const onKeyDown = () => {
    lastKeyAt = performance.now();
  };
  document.addEventListener("pointerdown", onPointerDown, { capture: true, passive: true });
  document.addEventListener("touchstart", onPointerDown, { capture: true, passive: true });
  document.addEventListener("keydown", onKeyDown, { capture: true, passive: true });

  const originalFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function guardedFocus(this: HTMLElement, options?: FocusOptions) {
    const now = performance.now();
    const block = shouldBlockTouchFocus({
      touchPrimary: isTouchPrimary(),
      opensKeyboard: opensVirtualKeyboard(this),
      optedIn: !!this.closest(`[${TOUCH_AUTOFOCUS_ATTR}]`),
      tappedOwn: !!lastTap && now - lastTap.at <= TOUCH_FOCUS_INTENT_WINDOW_MS && tapTargetsOwn(lastTap.target, this),
      keyRecent: now - lastKeyAt <= TOUCH_FOCUS_INTENT_WINDOW_MS,
      keyboardAlreadyUp: document.activeElement !== this && opensVirtualKeyboard(document.activeElement),
    });
    if (block) return;
    originalFocus.call(this, options);
  };

  const uninstall = () => {
    HTMLElement.prototype.focus = originalFocus;
    document.removeEventListener("pointerdown", onPointerDown, { capture: true });
    document.removeEventListener("touchstart", onPointerDown, { capture: true });
    document.removeEventListener("keydown", onKeyDown, { capture: true });
    delete w[GUARD_KEY];
  };
  w[GUARD_KEY] = uninstall;
  return uninstall;
}
